// Lightweight WebAudio sound effects (no external assets)
let ctx = null;
let muted = false;

const getCtx = () => {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
};

export const setMuted = (v) => {
  muted = !!v;
  // When muting globally, immediately silence any long-running audio
  // sources (currently the tavern0.mp3 ambient loop). Procedural BGMs
  // honour the flag on their next scheduled note.
  if (muted) {
    try { if (_tavernAudio) _tavernAudio.pause(); } catch {}
  } else {
    // Resume tavern loop only if a stop handle exists (i.e. the user
    // is still inside the Tavern route).
    try { if (_tavernStop && _tavernAudio) { const p = _tavernAudio.play(); if (p && p.catch) p.catch(() => {}); } } catch {}
  }
};
export const isMuted = () => muted;

const beep = ({ freq = 440, duration = 0.08, type = "square", volume = 0.08, slideTo = null }) => {
  if (muted) return;
  const ac = getCtx();
  if (!ac) return;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ac.currentTime);
  if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, ac.currentTime + duration);
  gain.gain.setValueAtTime(volume, ac.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + duration);
  osc.connect(gain).connect(ac.destination);
  osc.start();
  osc.stop(ac.currentTime + duration);
};

// Synthesize a wooden ball striking a wooden paddle: short pitched
// tone + filtered noise burst with very fast decay. No oscillator beep.
const woodHit = () => {
  if (muted) return;
  const ac = getCtx();
  if (!ac) return;
  const t0 = ac.currentTime;

  // Pitched body — low square that drops a third for a hollow thunk.
  const osc = ac.createOscillator();
  const oscGain = ac.createGain();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(220, t0);
  osc.frequency.exponentialRampToValueAtTime(110, t0 + 0.08);
  oscGain.gain.setValueAtTime(0.18, t0);
  oscGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.09);
  osc.connect(oscGain).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + 0.1);

  // Noise transient — buffer of short white noise pushed through a band-pass
  // filter to mimic the impact's percussive crack.
  const buf = ac.createBuffer(1, ac.sampleRate * 0.05, ac.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const src = ac.createBufferSource();
  src.buffer = buf;
  const bp = ac.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 1800;
  bp.Q.value = 1.4;
  const noiseGain = ac.createGain();
  noiseGain.gain.setValueAtTime(0.22, t0);
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.05);
  src.connect(bp).connect(noiseGain).connect(ac.destination);
  src.start(t0);
  src.stop(t0 + 0.05);
};

export const sfx = {
  paddle: woodHit,
  wall: () => beep({ freq: 320, duration: 0.05, type: "square", volume: 0.05 }),
  score: () => {
    beep({ freq: 660, duration: 0.12, type: "sawtooth", volume: 0.08, slideTo: 220 });
  },
  powerup: () => {
    beep({ freq: 880, duration: 0.08, type: "triangle", volume: 0.08 });
    setTimeout(() => beep({ freq: 1320, duration: 0.1, type: "triangle", volume: 0.07 }), 70);
  },
  win: () => {
    beep({ freq: 523, duration: 0.12, type: "square", volume: 0.08 });
    setTimeout(() => beep({ freq: 659, duration: 0.12, type: "square", volume: 0.08 }), 130);
    setTimeout(() => beep({ freq: 784, duration: 0.18, type: "square", volume: 0.08 }), 260);
  },
  // Bong pipe gurgle: a sequence of short low blub-blub bubble blips with
  // slight pitch drift, evoking water boiling / a bong being hit.
  bubble: () => {
    if (muted) return;
    const ac = getCtx();
    if (!ac) return;
    const burst = (delay, freq, dur) => setTimeout(() => {
      beep({ freq, duration: dur, type: "sine", volume: 0.07, slideTo: freq * 0.6 });
    }, delay);
    burst(0,    240, 0.18);
    burst(120,  200, 0.16);
    burst(260,  180, 0.20);
    burst(420,  220, 0.14);
    burst(560,  160, 0.22);
    burst(720,  200, 0.16);
  },
};

// ============================================================================
// Themed UI click sounds — one synth per page-theme. Called by `playClick(theme)`
// or via the convenience hooks in `useThemedClick`.
// ============================================================================

// "studio" — modern minimalist UI tap (sine pop, gentle).
const studioClick = () => {
  if (muted) return;
  const ac = getCtx();
  if (!ac) return;
  const t0 = ac.currentTime;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(880, t0);
  osc.frequency.exponentialRampToValueAtTime(440, t0 + 0.07);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(0.05, t0 + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.08);
  osc.connect(g).connect(ac.destination);
  osc.start(t0); osc.stop(t0 + 0.09);
};

// ---------- 15-puzzle tile-slide ----------
// Per user iter 55: tile movement now sounds like a rock being
// dragged across stone — a low, gritty scrape with a brief impact at
// the end where the rock settles.  Two layers:
//   • SCRAPE  — band-passed pink noise, 90-180 ms, slowly sweeping
//     band-pass centre from 220 Hz to ~480 Hz (low-mid grind).  Volume
//     fades in over 30 ms then sits flat until ~60 ms, then trails.
//   • SETTLE  — short low triangle thump at the very end (90 Hz, 50 ms
//     decay) suggesting the rock dropping into place.
// Distinct from any door-knock or knuckle-knock variant in this file.
export const playFifteenSlide = () => {
  if (muted) return;
  const ac = getCtx();
  if (!ac) return;
  const t0 = ac.currentTime + 0.001;

  // ----- Scrape -----
  const dur = 0.140;
  const len = Math.floor(ac.sampleRate * dur);
  const buf = ac.createBuffer(1, len, ac.sampleRate);
  const d = buf.getChannelData(0);
  let lp = 0;
  for (let i = 0; i < len; i++) {
    const w = Math.random() * 2 - 1;
    lp = 0.92 * lp + 0.08 * w;       // crude pink filter
    d[i] = lp * 6;
  }
  const src = ac.createBufferSource(); src.buffer = buf;
  const bp = ac.createBiquadFilter();
  bp.type = "bandpass"; bp.Q.value = 1.6;
  bp.frequency.setValueAtTime(220, t0);
  bp.frequency.exponentialRampToValueAtTime(480, t0 + dur);
  // Light high-frequency roll-off — keep it dirty, not sizzling.
  const lpFilt = ac.createBiquadFilter();
  lpFilt.type = "lowpass"; lpFilt.frequency.value = 2200;
  const g = ac.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(0.55, t0 + 0.025);
  g.gain.setValueAtTime(0.55, t0 + dur * 0.55);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(bp).connect(lpFilt).connect(g).connect(ac.destination);
  src.start(t0); src.stop(t0 + dur + 0.04);

  // ----- Settle thump -----
  const settleAt = t0 + dur - 0.020;
  const o = ac.createOscillator();
  const og = ac.createGain();
  o.type = "triangle";
  o.frequency.setValueAtTime(120, settleAt);
  o.frequency.exponentialRampToValueAtTime(70, settleAt + 0.05);
  og.gain.setValueAtTime(0.0001, settleAt);
  og.gain.exponentialRampToValueAtTime(0.18, settleAt + 0.005);
  og.gain.exponentialRampToValueAtTime(0.0001, settleAt + 0.06);
  o.connect(og).connect(ac.destination);
  o.start(settleAt); o.stop(settleAt + 0.07);
};


// Chess and Checkers use a sharper KNOCK-ON-DOOR thud per user iter 51.
//
// ============================================================================
// Piece-placement sound — per user iter 67, the procedural Pente
// `playGemPlace` and the Chess/Checkers `playWoodPlace` are both
// replaced with the user-supplied `rock_strike.mp3` file.  Same
// audible result for all three games.
//
// We keep a small pool of `HTMLAudioElement`s so successive moves
// (e.g. in fast Pente play) don't get clipped while a previous strike
// is still ringing — the next call grabs the next free element.
// ============================================================================
const ROCK_SRC = "/audio/rock_strike.mp3";
const ROCK_POOL_SIZE = 4;
const _rockPool = [];
let _rockNext = 0;

function _getRockAudio() {
  if (_rockPool.length === 0) {
    for (let i = 0; i < ROCK_POOL_SIZE; i++) {
      const a = new Audio(ROCK_SRC);
      a.preload = "auto";
      a.crossOrigin = "anonymous";
      a.volume = 1.0;
      _rockPool.push(a);
    }
  }
  const a = _rockPool[_rockNext];
  _rockNext = (_rockNext + 1) % ROCK_POOL_SIZE;
  return a;
}

function _playRockStrike() {
  if (muted) return;
  try {
    const a = _getRockAudio();
    try { a.currentTime = 0; } catch {}
    const p = a.play();
    if (p && p.catch) p.catch(() => {});
  } catch {}
}

export const playGemPlace  = _playRockStrike;
export const playWoodPlace = _playRockStrike;

// "tavern" — a single heavy knock on a wooden door (sharp tonk attack + short resonance).
const tavernClick = () => {
  if (muted) return;
  const ac = getCtx();
  if (!ac) return;
  const t0 = ac.currentTime;
  // Body thump — low sine with fast exponential decay
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(110, t0);
  osc.frequency.exponentialRampToValueAtTime(55, t0 + 0.12);
  g.gain.setValueAtTime(0.34, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.14);
  osc.connect(g).connect(ac.destination);
  osc.start(t0); osc.stop(t0 + 0.15);
  // Crisp knock transient — filtered noise burst
  const buf = ac.createBuffer(1, ac.sampleRate * 0.025, ac.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const src = ac.createBufferSource();
  src.buffer = buf;
  const bp = ac.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 600;
  bp.Q.value = 2.2;
  const ng = ac.createGain();
  ng.gain.setValueAtTime(0.28, t0);
  ng.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.025);
  src.connect(bp).connect(ng).connect(ac.destination);
  src.start(t0); src.stop(t0 + 0.025);
};

// "wupong" — hip-hop turntable scratch + 808 thump for the Wu-Pong screens.
const wupongClick = () => {
  if (muted) return;
  const ac = getCtx();
  if (!ac) return;
  const t0 = ac.currentTime;
  // 808 sub-thump
  const sub = ac.createOscillator();
  const subG = ac.createGain();
  sub.type = "sine";
  sub.frequency.setValueAtTime(120, t0);
  sub.frequency.exponentialRampToValueAtTime(40, t0 + 0.16);
  subG.gain.setValueAtTime(0.28, t0);
  subG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
  sub.connect(subG).connect(ac.destination);
  sub.start(t0); sub.stop(t0 + 0.19);
  // Vinyl scratch — short FM-style chirp
  const sc = ac.createOscillator();
  const scG = ac.createGain();
  sc.type = "sawtooth";
  sc.frequency.setValueAtTime(900, t0);
  sc.frequency.exponentialRampToValueAtTime(280, t0 + 0.09);
  scG.gain.setValueAtTime(0.07, t0);
  scG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.1);
  const lp = ac.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 2400;
  sc.connect(lp).connect(scG).connect(ac.destination);
  sc.start(t0); sc.stop(t0 + 0.1);
};

// "fifteen" — uses the same /sounds/knock.mp3 sample as the NavPane
// (per user iter 125).  Every button click on the 15-tile page plays
// this knock; the NavPane buttons themselves remain data-silent so
// they don't double-trigger the page-theme click.
const fifteenClick = () => {
  if (muted) return;
  const el = _getKnockEl();
  if (!el) return;
  try {
    el.currentTime = 0;
    const p = el.play();
    if (p && typeof p.then === "function") p.catch(() => {});
  } catch {}
};

const _clickByTheme = {
  studio: studioClick,
  tavern: tavernClick,
  wupong: wupongClick,
  fifteen: fifteenClick,
  // Iter 128: alias for routes that want the same /sounds/knock.mp3
  // sample as the NavPane (chess + checkers pages).
  knock: fifteenClick,
};

export const playClick = (theme = "studio") => {
  const fn = _clickByTheme[theme] || studioClick;
  try { fn(); } catch {}
};

// ============================================================================
// NavPane interaction SFX (per user iter 115).
//   playNavHover() — soft brush / friction noise (fabric-on-fabric)
//   playNavClick() — sharp, dry finger-snap
// Both are short, low-volume, and safely no-op when muted or when the
// AudioContext can't be resumed (Safari user-gesture rules respected via
// getCtx()'s try-resume).
// ============================================================================

// Throttle the hover sound so quickly skimming the menu doesn't spam audio.
let _lastBrushAt = 0;
export const playNavHover = () => {
  if (muted) return;
  const ac = getCtx();
  if (!ac) return;
  const now = ac.currentTime;
  if (now - _lastBrushAt < 0.10) return;     // ~10 brushes / sec cap
  _lastBrushAt = now;
  try {
    // Iter 116: ultra-soft "breath" — very low-volume filtered noise puff.
    const dur = 0.08;
    const buf = ac.createBuffer(1, Math.floor(ac.sampleRate * dur), ac.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const t = i / data.length;
      // Bell-ish window: quick rise (~6ms), gentle decay.
      const env = Math.min(1, t / 0.07) * Math.pow(1 - t, 2.2);
      data[i] = (Math.random() * 2 - 1) * env;
    }
    const src = ac.createBufferSource();
    src.buffer = buf;
    // Low-pass at 1.6kHz takes the sting out — sounds like a soft puff
    // rather than the previous 5kHz papery hiss.
    const lp = ac.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 1600;
    lp.Q.value = 0.5;
    const g = ac.createGain();
    // Drop from 0.08 to 0.022 — roughly a quarter as loud.
    g.gain.setValueAtTime(0.022, now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    src.connect(lp).connect(g).connect(ac.destination);
    src.start(now);
    src.stop(now + dur + 0.02);
  } catch {}
};

// Iter 117 (revised): nav-click plays the user-supplied
// /sounds/knock.mp3 sample.  One pre-loaded HTMLAudioElement, rewound
// and replayed on each click.
let _knockEl = null;
const _getKnockEl = () => {
  if (typeof window === "undefined") return null;
  if (!_knockEl) {
    try {
      _knockEl = new Audio("/sounds/knock.mp3");
      _knockEl.preload = "auto";
      // Iter 122: nudged up a notch from 0.08 -> 0.13 per user request.
      _knockEl.volume = 0.13;
    } catch { return null; }
  }
  return _knockEl;
};

export const playNavClick = () => {
  if (muted) return;
  const el = _getKnockEl();
  if (!el) return;
  try {
    el.currentTime = 0;
    const p = el.play();
    if (p && typeof p.then === "function") p.catch(() => {});
  } catch {}
};

// ============================================================================
// 15-tile WIN soundscape (per user iter 124 — creepier, subtler take).
//   ~4.5 seconds.  Pure WebAudio.  No chord, no bell cascade — just a
//   single barely-there held drone, a slow filtered-noise "breath",
//   and a single distant low knock buried in reverb tail.  Master mix
//   peaks ~0.30 (vs 0.55 in iter 123) so it sits as ambience, not music.
// ============================================================================
export const playFifteenWin = () => {
  if (muted) return;
  const ac = getCtx();
  if (!ac) return;
  const t0 = ac.currentTime;
  const total = 4.6;
  try {
    // Master bus — gentle in/out envelope, low peak.
    const master = ac.createGain();
    master.gain.setValueAtTime(0.0, t0);
    master.gain.linearRampToValueAtTime(0.30, t0 + 0.35);
    master.gain.setValueAtTime(0.30, t0 + total - 1.1);
    master.gain.linearRampToValueAtTime(0.0, t0 + total);
    master.connect(ac.destination);

    // Soft reverb tail — feedback delay used by the knock + breath wash.
    const delay = ac.createDelay(2.0);
    delay.delayTime.value = 0.38;
    const fb = ac.createGain();
    fb.gain.value = 0.60;
    delay.connect(fb).connect(delay);
    const wet = ac.createGain();
    wet.gain.value = 0.38;
    delay.connect(wet).connect(master);

    // --- Layer 1: held drone (very soft) -----------------------------------
    // One detuned pair of sines around a low D2 / D2+8¢ — slow beating
    // gives a queasy held-breath feel without sounding "musical".
    [73.42, 73.42].forEach((f, i) => {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = "sine";
      o.frequency.value = f;
      o.detune.value = i === 0 ? -7 : +9;
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(0.16, t0 + 1.0);
      g.gain.setValueAtTime(0.16, t0 + total - 1.4);
      g.gain.linearRampToValueAtTime(0.0, t0 + total - 0.2);
      o.connect(g).connect(master);
      o.start(t0); o.stop(t0 + total);
    });

    // --- Layer 2: slow filtered-noise "breath" -----------------------------
    // A long noise buffer played through a sweeping low-pass — sounds
    // like a slow exhale somewhere offscreen.  Routed BOTH dry (very
    // quiet) and into the reverb network for spatial wash.
    const dur = total;
    const buf = ac.createBuffer(1, Math.floor(ac.sampleRate * dur), ac.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
    const src = ac.createBufferSource();
    src.buffer = buf;
    const sweep = ac.createBiquadFilter();
    sweep.type = "lowpass";
    sweep.frequency.setValueAtTime(2200, t0);
    sweep.frequency.exponentialRampToValueAtTime(380, t0 + total - 0.4);
    sweep.Q.value = 0.7;
    const ng = ac.createGain();
    ng.gain.setValueAtTime(0.0, t0);
    ng.gain.linearRampToValueAtTime(0.08, t0 + 0.6);
    ng.gain.linearRampToValueAtTime(0.045, t0 + total - 0.6);
    ng.gain.linearRampToValueAtTime(0.0, t0 + total - 0.1);
    src.connect(sweep).connect(ng);
    ng.connect(master);
    ng.connect(delay);            // throws the breath into the reverb tail
    src.start(t0); src.stop(t0 + total);

    // --- Layer 3: one distant low knock around 1.6 s in --------------------
    // A single short low-mid thunk that bleeds into the reverb — one
    // faint footstep / door-bump somewhere far away.  No second hit, no
    // pattern.
    const knockAt = t0 + 1.55;
    const ko = ac.createOscillator();
    const kg = ac.createGain();
    ko.type = "sine";
    ko.frequency.setValueAtTime(130, knockAt);
    ko.frequency.exponentialRampToValueAtTime(60, knockAt + 0.20);
    kg.gain.setValueAtTime(0, knockAt);
    kg.gain.linearRampToValueAtTime(0.18, knockAt + 0.01);
    kg.gain.exponentialRampToValueAtTime(0.0001, knockAt + 0.22);
    ko.connect(kg);
    kg.connect(master);
    kg.connect(delay);            // washes into the tail
    ko.start(knockAt); ko.stop(knockAt + 0.24);

    // Tight noise transient on the knock — band-passed around 380 Hz so
    // it reads as "wood/door from another room" rather than a clean
    // percussion hit.
    const tDur = 0.022;
    const tBuf = ac.createBuffer(1, Math.floor(ac.sampleRate * tDur), ac.sampleRate);
    const tData = tBuf.getChannelData(0);
    for (let i = 0; i < tData.length; i++) {
      const t = i / tData.length;
      const env = (t < 0.05 ? t / 0.05 : 1) * Math.pow(1 - t, 1.4);
      tData[i] = (Math.random() * 2 - 1) * env;
    }
    const tSrc = ac.createBufferSource();
    tSrc.buffer = tBuf;
    const tbp = ac.createBiquadFilter();
    tbp.type = "bandpass";
    tbp.frequency.value = 380;
    tbp.Q.value = 1.6;
    const tng = ac.createGain();
    tng.gain.setValueAtTime(0.12, knockAt);
    tng.gain.exponentialRampToValueAtTime(0.0001, knockAt + tDur);
    tSrc.connect(tbp).connect(tng);
    tng.connect(master);
    tng.connect(delay);
    tSrc.start(knockAt); tSrc.stop(knockAt + tDur + 0.005);
  } catch {}
};

// ============================================================================
// Bong background loop — GOOFY CARTOON / OOMPAH groove (per user iter 56,
// replacing the iter-52..55 psychedelic-rock + lap-steel build).
// Layers:
//   • Tuba "OOM-PAH" bass: alternating low root note (sine "boom") and a
//     short staccato fifth ("pah"), 1-2-1-2 throughout the bar
//   • Marimba/xylophone melody: square+triangle stacks playing a comedic
//     C-major arpeggio with grace notes and an occasional "wrong note"
//     bend for cartoon mischief
//   • Slide whistle accents every 4-8 bars: smooth sine swoop up or
//     down covering an octave for that classic Looney-Tunes flavour
// Tempo 124 BPM, 4/4 swing. No reverb, no chorus — dry & silly.
// ============================================================================
let _wupongStop = null;

export const startWupongBackground = () => {
  if (_wupongStop) return;
  const ac = getCtx();
  if (!ac) return;

  // Per user iter 62: slowed further to 60 BPM (was 80 in iter 57)
  // — a leisurely vaudeville plod.
  const bpm = 60;
  const beat = 60 / bpm;
  const sixteenth = beat / 4;
  const barLen = 16 * sixteenth;

  const masterGain = ac.createGain();
  masterGain.gain.value = 0.0;
  masterGain.connect(ac.destination);
  masterGain.gain.linearRampToValueAtTime(0.32, ac.currentTime + 1.0);

  // ---------- Tuba "oom" — round low sine with slight pitch droop -----
  const tuba = (f, t, dur, peak = 0.32) => {
    const o = ac.createOscillator(); const g = ac.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(f * 1.02, t);
    o.frequency.exponentialRampToValueAtTime(f, t + 0.05);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.012);
    g.gain.exponentialRampToValueAtTime(peak * 0.55, t + dur * 0.6);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(masterGain);
    o.start(t); o.stop(t + dur + 0.04);
  };

  // ---------- "Pah" — short triangle staccato chord stab -------------
  const pah = (root, t, peak = 0.10) => {
    const stack = [root, root * 1.5, root * 2.0]; // root + 5th + octave
    stack.forEach((f) => {
      const o = ac.createOscillator(); const g = ac.createGain();
      o.type = "triangle";
      o.frequency.setValueAtTime(f, t);
      const lp = ac.createBiquadFilter();
      lp.type = "lowpass"; lp.frequency.value = 1800; lp.Q.value = 0.8;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(peak, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.10);
      o.connect(lp).connect(g).connect(masterGain);
      o.start(t); o.stop(t + 0.12);
    });
  };

  // ---------- Marimba/xylophone melody --------------------------------
  // Square+triangle stack, fast attack, ~250ms decay — bright wood-stick.
  const marimba = (f, t, dur = 0.32, peak = 0.18) => {
    const make = (type, mult, amp) => {
      const o = ac.createOscillator(); const g = ac.createGain();
      o.type = type;
      o.frequency.setValueAtTime(f * mult, t);
      const bp = ac.createBiquadFilter();
      bp.type = "bandpass"; bp.frequency.value = f * 4; bp.Q.value = 1.6;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(amp, t + 0.004);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.connect(bp).connect(g).connect(masterGain);
      o.start(t); o.stop(t + dur + 0.04);
    };
    make("square",   1.0, peak * 0.55);
    make("triangle", 2.0, peak * 0.30);
    make("sine",     4.0, peak * 0.15);
  };

  // ---------- Slide whistle accent ------------------------------------
  const slideWhistle = (t, up = true) => {
    const dur = 0.65;
    const o = ac.createOscillator(); const g = ac.createGain();
    o.type = "sine";
    const f0 = up ? 440 : 1320;
    const f1 = up ? 1320 : 440;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f1, t + dur * 0.85);
    o.frequency.exponentialRampToValueAtTime(f1 * 0.97, t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.10, t + 0.04);
    g.gain.setValueAtTime(0.10, t + dur * 0.7);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(masterGain);
    o.start(t); o.stop(t + dur + 0.04);
  };

  // ---------- Note dictionaries — comedic C-major --------------------
  // Bass progression: I - V - vi - IV (C - G - Am - F), 1 bar each.
  const BASS_ROOTS = [65.41, 49.00, 55.00, 43.65]; // C2, G1, A1, F1
  // Marimba phrase patterns (16 sixteenths each), playing in C-major.
  // Notes are SCALE indices; the "wrong note" b9 in pattern 3 is the
  // cartoon-mischief moment.
  const SCALE = [261.63, 293.66, 329.63, 349.23, 392.00, 440.00, 493.88, 523.25]; // C4..C5
  const MELODY_PATTERNS = [
    // Pattern A: silly skipping
    [0, -1, 2, -1, 4, -1, 2, -1, 0, 2, 4, 5, 4, 2, 0, -1],
    // Pattern B: ascending then giggle
    [0, 2, 4, 5, 7, 5, 4, 2, 0, 2, 4, 2, 4, 5, 4, -1],
    // Pattern C: WITH a "wrong note" stumble (idx -2 = below scale, b9)
    [4, 2, 0, -2, 0, 2, 4, -1, 5, 4, 2, 0, 2, 4, 5, -1],
    // Pattern D: trumpet-y triplets
    [4, 7, 4, 2, 0, 2, 4, 7, 4, 2, 0, -1, 2, 4, 7, -1],
  ];

  let cancelled = false;
  let bar = 0;
  const startAt = ac.currentTime + 0.05;
  const _scheduleBar = (when, barIdx) => {
    // OOM-PAH bass — root on beats 1+3, fifth-stab "pah" on beats 2+4.
    const root = BASS_ROOTS[barIdx % BASS_ROOTS.length];
    const fifth = root * 1.5;
    tuba(root,  when + 0 * beat, beat * 0.8, 0.28);
    pah (fifth, when + 1 * beat, 0.10);
    tuba(root,  when + 2 * beat, beat * 0.8, 0.28);
    pah (fifth, when + 3 * beat, 0.10);

    // Marimba melody.
    const phrase = MELODY_PATTERNS[barIdx % MELODY_PATTERNS.length];
    phrase.forEach((idx, s) => {
      if (idx === -1) return; // rest
      let f;
      if (idx === -2) f = SCALE[0] * 0.943;     // "wrong note" b9 below C
      else            f = SCALE[Math.min(SCALE.length - 1, Math.max(0, idx))];
      const t = when + s * sixteenth;
      // grace-note flick on the "and" of beat 1
      if (s === 2) {
        marimba(f * 1.06, t - sixteenth * 0.4, 0.10, 0.10);
      }
      marimba(f, t, sixteenth * 1.6, 0.16);
    });

    // Slide whistle every 4 bars (alternating up/down)
    if (barIdx > 0 && barIdx % 4 === 0) {
      slideWhistle(when + barLen * 0.40, barIdx % 8 !== 0);
    }
  };

  const loop = () => {
    if (cancelled) return;
    const when = startAt + bar * barLen;
    if (when - ac.currentTime < 0.3) {
      _scheduleBar(when, bar);
      bar++;
    }
    _wupongTimer = setTimeout(loop, 150);
  };
  let _wupongTimer = setTimeout(loop, 10);

  _wupongStop = () => {
    cancelled = true;
    clearTimeout(_wupongTimer);
    try {
      masterGain.gain.cancelScheduledValues(ac.currentTime);
      masterGain.gain.setValueAtTime(masterGain.gain.value, ac.currentTime);
      masterGain.gain.linearRampToValueAtTime(0.0, ac.currentTime + 0.5);
      setTimeout(() => { try { masterGain.disconnect(); } catch {} }, 700);
    } catch {}
    _wupongStop = null;
  };
};


export const stopWupongBackground = () => {
  if (_wupongStop) _wupongStop();
};

// ============================================================================
// Angus and the Music Pantheon — late-80s MIDI background loop.
// Models AdLib / SoundBlaster OPL2 FM synthesis aesthetic:
//   • Lead voice: bright square wave with slight detuning + portamento
//   • Bass voice: pulse-width-ish sawtooth (heavy low-pass) running an
//     8-note bass line
//   • Drums: very simple TR-style kick + snare + hat (1990 SC-55 dance
//     drum kit feel — short, dry, no reverb)
//   • Tempo: 132 BPM (typical late-80s/early-90s game music)
//   • Key: A minor — lead arpeggios A C E A on a 4-bar repeat with a
//     bass walk-down progression Am - F - G - Em.
// All voices summed dry; very subtle "AdLib" character with no
// reverb / chorus.  Master gain stays low (0.22) so the music sits
// quietly under the page's UI rather than dominating it.
// ============================================================================
let _angusStop = null;

export const startAngusMidi = () => {
  if (_angusStop) return;
  const ac = getCtx();
  if (!ac) return;

  const bpm = 88; // slowed per user iter 54 (was 132)
  const beat = 60 / bpm;
  const sixteenth = beat / 4;
  const barLen = 16 * sixteenth;

  const master = ac.createGain();
  master.gain.value = 0.0;
  master.connect(ac.destination);
  master.gain.linearRampToValueAtTime(0.22, ac.currentTime + 1.5);

  // -------- Lead --------
  const leadSquare = (f, t, dur, peak = 0.10) => {
    const o1 = ac.createOscillator();
    const o2 = ac.createOscillator();
    o1.type = "square"; o2.type = "square";
    o1.frequency.value = f;
    o2.frequency.value = f;
    o1.detune.value = -6; o2.detune.value = +6;
    const lp = ac.createBiquadFilter();
    lp.type = "lowpass"; lp.frequency.value = Math.min(4500, f * 7); lp.Q.value = 0.5;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.012);
    g.gain.setValueAtTime(peak, t + dur * 0.55);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o1.connect(lp); o2.connect(lp); lp.connect(g).connect(master);
    o1.start(t); o2.start(t);
    o1.stop(t + dur + 0.03); o2.stop(t + dur + 0.03);
  };

  // -------- Bass (saw + low-pass) --------
  const bassNote = (f, t, dur, peak = 0.16) => {
    const o = ac.createOscillator();
    o.type = "sawtooth"; o.frequency.value = f;
    const lp = ac.createBiquadFilter();
    lp.type = "lowpass"; lp.frequency.value = 380; lp.Q.value = 1.6;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(peak, t + 0.015);
    g.gain.setValueAtTime(peak, t + dur * 0.7);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(lp).connect(g).connect(master);
    o.start(t); o.stop(t + dur + 0.04);
  };

  // -------- Drums --------
  const kick = (t) => {
    const o = ac.createOscillator(); const g = ac.createGain();
    o.type = "sine"; o.frequency.setValueAtTime(120, t);
    o.frequency.exponentialRampToValueAtTime(38, t + 0.15);
    g.gain.setValueAtTime(0.55, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
    o.connect(g).connect(master); o.start(t); o.stop(t + 0.20);
  };
  const snare = (t) => {
    const b = ac.createBuffer(1, ac.sampleRate * 0.08, ac.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const src = ac.createBufferSource(); src.buffer = b;
    const bp = ac.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 1700; bp.Q.value = 0.8;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.20, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
    src.connect(bp).connect(g).connect(master); src.start(t); src.stop(t + 0.09);
  };
  const hat = (t) => {
    const b = ac.createBuffer(1, ac.sampleRate * 0.03, ac.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const src = ac.createBufferSource(); src.buffer = b;
    const hp = ac.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 7500;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.06, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.03);
    src.connect(hp).connect(g).connect(master); src.start(t); src.stop(t + 0.035);
  };

  // -------- Note dictionaries (A minor) --------
  // A2=110, A3=220, C4=261.63, E4=329.63, G3=196.00, F3=174.61, E3=164.81
  // Per user iter M645: two distinct melodies in the same key, tempo
  // and bar count alternate as A, A, B, B, A, A, B, B...  Each melody
  // is 4 bars long.  MELODY_A is the original arpeggio set; MELODY_B
  // is a contrasting descending phrase in the same A-minor scale that
  // still resolves around the chord progression Am - F - G - Em.
  const MELODY_A = [
    [440.00, 523.25, 659.25, 523.25, 440.00, 0, 392.00, 440.00, 523.25, 659.25, 783.99, 659.25, 523.25, 440.00, 392.00, 0],
    [440.00, 523.25, 659.25, 698.46, 659.25, 523.25, 440.00, 0, 392.00, 440.00, 523.25, 587.33, 523.25, 440.00, 392.00, 349.23],
    [659.25, 587.33, 523.25, 440.00, 392.00, 440.00, 523.25, 659.25, 587.33, 523.25, 440.00, 0, 392.00, 440.00, 523.25, 587.33],
    [440.00, 0, 523.25, 0, 659.25, 0, 783.99, 0, 659.25, 0, 523.25, 0, 440.00, 0, 392.00, 0],
  ];
  // MELODY_B — descending stepwise phrase (E5 down to A3) with a
  // contrasting rhythm: a long held note on beats 1-2 of each bar,
  // then a quick 8th-note tail. Same key, same 88 BPM, 4 bars.
  const MELODY_B = [
    [659.25, 0, 0, 0, 587.33, 0, 523.25, 0, 493.88, 0, 440.00, 0, 392.00, 0, 349.23, 0],
    [349.23, 0, 0, 0, 392.00, 0, 440.00, 0, 493.88, 0, 523.25, 0, 587.33, 0, 659.25, 0],
    [392.00, 0, 0, 0, 440.00, 0, 523.25, 0, 587.33, 0, 523.25, 0, 440.00, 0, 392.00, 0],
    [330.00, 0, 0, 0, 349.23, 0, 392.00, 0, 440.00, 0, 392.00, 0, 349.23, 0, 330.00, 0],
  ];
  // A A B B cycle, 4-bar melody each → 16-bar full cycle.
  const MELODIES = [MELODY_A, MELODY_A, MELODY_B, MELODY_B];
  const MELODY_LEN = 4; // bars per melody pass
  // Bass — chord progression Am - F - G - Em (1 bar each), shared by
  // both melodies so they sound like contrasting verses of the same
  // piece.
  const BASS_ROOTS = [110.00, 87.31, 98.00, 82.41]; // A2, F2, G2, E2

  let cancelled = false;
  let bar = 0;
  const startAt = ac.currentTime + 0.05;
  const _scheduleBar = (when, barIdx) => {
    // A-A-B-B rotation: each melody is 4 bars; the rotation cycles
    // every 4 melody passes × 4 bars = 16 bars.
    const melodyPass = Math.floor(barIdx / MELODY_LEN) % MELODIES.length;
    const currentMelody = MELODIES[melodyPass];
    const lead = currentMelody[barIdx % MELODY_LEN];
    lead.forEach((freq, i) => {
      if (freq > 0) {
        const t = when + i * sixteenth;
        leadSquare(freq, t, sixteenth * 0.95, 0.085);
      }
    });
    // Bass — quarter-note pulse at the chord root.  Same Am-F-G-Em
    // 4-bar progression under both melodies.
    const root = BASS_ROOTS[barIdx % BASS_ROOTS.length];
    for (let q = 0; q < 4; q++) {
      const t = when + q * beat;
      bassNote(root, t, beat * 0.92, 0.16);
    }
    // Drums — kick on 1 & 3, snare on 2 & 4, hat on every 8th.
    for (let s = 0; s < 16; s++) {
      const t = when + s * sixteenth;
      if (s === 0 || s === 8) kick(t);
      if (s === 4 || s === 12) snare(t);
      if (s % 2 === 0) hat(t);
    }
  };

  const loop = () => {
    if (cancelled) return;
    const when = startAt + bar * barLen;
    if (when - ac.currentTime < 0.4) {
      _scheduleBar(when, bar);
      bar++;
    }
    _angusTimer = setTimeout(loop, 150);
  };
  let _angusTimer = setTimeout(loop, 10);

  _angusStop = () => {
    cancelled = true;
    clearTimeout(_angusTimer);
    try {
      master.gain.cancelScheduledValues(ac.currentTime);
      master.gain.setValueAtTime(master.gain.value, ac.currentTime);
      master.gain.linearRampToValueAtTime(0.0, ac.currentTime + 0.5);
      setTimeout(() => { try { master.disconnect(); } catch {} }, 700);
    } catch {}
    _angusStop = null;
  };
};

export const stopAngusMidi = () => {
  if (_angusStop) _angusStop();
};



// ============================================================================
// Tavern background — per user iter M631, the procedural waves + bell
// patch is replaced with the user-supplied /sounds/tavern0.mp3 file
// played on loop. Honours the global mute flag and the persisted
// `studio:muted` localStorage key so refreshing doesn't override the
// user's preference.
// ============================================================================
let _tavernAudio = null;
let _tavernStop = null;

export const startTavernBackground = () => {
  if (_tavernStop) return;
  // Honour the global mute toggle.
  if (typeof window !== "undefined" && window.localStorage) {
    if (window.localStorage.getItem("studio:muted") === "1") return;
  }
  if (muted) return;
  if (typeof window === "undefined") return;

  try {
    if (!_tavernAudio) {
      _tavernAudio = new Audio("/sounds/tavern6.mp3");
      _tavernAudio.preload = "auto";
      _tavernAudio.loop = true;
      _tavernAudio.volume = 0.5;
    }
    _tavernAudio.currentTime = 0;
    const p = _tavernAudio.play();
    if (p && typeof p.then === "function") p.catch(() => {});
  } catch {}

  _tavernStop = () => {
    try {
      if (_tavernAudio) {
        _tavernAudio.pause();
      }
    } catch {}
    _tavernStop = null;
  };
};

export const stopTavernBackground = () => {
  if (_tavernStop) _tavernStop();
};



